import { SESSION_REPLAY_LIMITS, type SessionReplaySignal } from "@backchannel/tax-contracts";
import { scrubSignal } from "./scrub";

type NetworkFailure = {
  requestId: string;
  method: string;
  path: string;
};

type RouterLike = {
  subscribe: (
    event: "onResolved",
    listener: (opts: { toLocation: { pathname: string } }) => void,
  ) => () => void;
  state?: { location?: { pathname?: string } };
};

const MAX_QUEUED = 200;
const DEDUPE_CAP = 50;

type DedupEntry = { count: number; signal: SessionReplaySignal };

const queue: SessionReplaySignal[] = [];
const dedupe = new Map<string, DedupEntry>();
let lastPathname: string | null = null;
let installed = false;
let disposeRouter: (() => void) | null = null;
let accepting = true;

function pathnameOnly(path: string): string {
  const cut = path.search(/[?#]/);
  const raw = cut === -1 ? path : path.slice(0, cut);
  return raw.startsWith("/") ? raw : `/${raw}`;
}

function isExtensionSource(source: string | undefined): boolean {
  if (!source) return false;
  return source.startsWith("chrome-extension://") || source.startsWith("moz-extension://");
}

function firstStackFrame(stack: string | undefined): string {
  if (!stack) return "";
  const lines = stack.split("\n").map((line) => line.trim());
  return lines.find((line) => line.length > 0 && !line.startsWith("Error")) ?? lines[0] ?? "";
}

function pushSignal(raw: SessionReplaySignal): void {
  try {
    if (!accepting) return;
    const signal = scrubSignal(raw);
    const key = `${signal.kind}|${signal.message ?? ""}|${firstStackFrame(signal.stack)}`;
    const existing = dedupe.get(key);
    if (existing) {
      if (existing.count >= DEDUPE_CAP) return;
      existing.count += 1;
      return;
    }
    dedupe.set(key, { count: 1, signal });
    queue.push(signal);
    while (queue.length > MAX_QUEUED) {
      const dropped = queue.shift();
      if (dropped) {
        const dropKey = `${dropped.kind}|${dropped.message ?? ""}|${firstStackFrame(dropped.stack)}`;
        dedupe.delete(dropKey);
      }
    }
  } catch {
    /* never throw */
  }
}

function currentRoute(): string | undefined {
  try {
    if (typeof window === "undefined") return undefined;
    return pathnameOnly(window.location.pathname);
  } catch {
    return undefined;
  }
}

function onWindowError(event: ErrorEvent): void {
  try {
    if (isExtensionSource(event.filename)) return;
    pushSignal({
      kind: "CLIENT_ERROR",
      occurredAt: new Date().toISOString(),
      route: currentRoute(),
      message: event.message || "error",
      source: event.filename || undefined,
      line: Number.isFinite(event.lineno) ? event.lineno : undefined,
      column: Number.isFinite(event.colno) ? event.colno : undefined,
      stack: event.error instanceof Error ? event.error.stack : undefined,
    });
  } catch {
    /* never throw */
  }
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  try {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === "string"
          ? reason
          : "unhandledrejection";
    const stack = reason instanceof Error ? reason.stack : undefined;
    const source = stack?.match(/(chrome-extension:\/\/|moz-extension:\/\/)/) ? stack : undefined;
    if (source && isExtensionSource(source)) return;
    pushSignal({
      kind: "UNHANDLED_REJECTION",
      occurredAt: new Date().toISOString(),
      route: currentRoute(),
      message,
      stack,
    });
  } catch {
    /* never throw */
  }
}

function recordNavigation(pathname: string): void {
  try {
    const route = pathnameOnly(pathname);
    if (route === lastPathname) return;
    lastPathname = route;
    pushSignal({
      kind: "NAVIGATION",
      occurredAt: new Date().toISOString(),
      route,
      message: route,
    });
  } catch {
    /* never throw */
  }
}

/** Record a Tax API request that failed before an answer (offline / timeout). */
export function recordNetworkFailure(info: NetworkFailure): void {
  try {
    const method = info.method.toUpperCase();
    if (!["GET", "POST", "PUT", "PATCH", "DELETE"].includes(method)) return;
    pushSignal({
      kind: "NETWORK_ERROR",
      occurredAt: new Date().toISOString(),
      route: currentRoute(),
      requestId: info.requestId.slice(0, 128),
      method: method as SessionReplaySignal["method"],
      path: pathnameOnly(info.path),
      message: `${method} ${pathnameOnly(info.path)}`,
    });
  } catch {
    /* never throw */
  }
}

/** Allow or refuse new signals (server kill switch for activity log). */
export function setSignalsAccepting(enabled: boolean): void {
  accepting = enabled;
  if (!enabled) {
    queue.length = 0;
    dedupe.clear();
  }
}

/**
 * Drain up to `max` signals (default: contract max per chunk). Cleared from
 * the queue; dedupe entries for drained items are removed.
 */
export function drainSignals(
  max = SESSION_REPLAY_LIMITS.maxSignalsPerChunk,
): SessionReplaySignal[] {
  const count = Math.min(Math.max(max, 0), queue.length);
  const drained = queue.splice(0, count);
  for (const signal of drained) {
    const key = `${signal.kind}|${signal.message ?? ""}|${firstStackFrame(signal.stack)}`;
    dedupe.delete(key);
  }
  return drained;
}

export function pendingSignalCount(): number {
  return queue.length;
}

/** Eager, tiny installer: window errors + router navigations. No rrweb. */
export function installReplaySignals(router?: RouterLike): () => void {
  if (installed || typeof window === "undefined") {
    return () => undefined;
  }
  installed = true;
  window.addEventListener("error", onWindowError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);

  // A full page load (reload, typed URL, link from outside the app) lands on a page the router
  // never navigated to: record it, or the timeline loses every page reached that way.
  try {
    recordNavigation(window.location.pathname);
  } catch {
    lastPathname = null;
  }

  if (router) {
    try {
      disposeRouter = router.subscribe("onResolved", (event) => {
        recordNavigation(event.toLocation.pathname);
      });
    } catch {
      disposeRouter = null;
    }
  }

  return () => {
    if (!installed) return;
    installed = false;
    window.removeEventListener("error", onWindowError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
    disposeRouter?.();
    disposeRouter = null;
  };
}

/** Test helper. */
export function resetReplaySignalsForTests(): void {
  queue.length = 0;
  dedupe.clear();
  lastPathname = null;
  installed = false;
  disposeRouter = null;
  accepting = true;
}
